/**
 * The Settings tab's form model (docs/plans/oncall-line-plan.md 2.3, 4 layer
 * 1, 7.5 PATCH /line, 7.7; WP-11), pure so it is tested on its own.
 *
 *   formFromLine    the line as GET /line answers it (settings clamped, texts
 *                   resolved to the defaults when not set) -> the form
 *   validateForm    per-field messages; the page sends nothing while any
 *                   remain (the server would clamp a number out of range
 *                   silently, plan 16 WP-06; the form says so instead)
 *   patchFromForm   only what changed, as PATCH /line takes it: { name?,
 *                   fallback_note?, settings?: { changed keys } }, or null
 *
 * record_calls is never sent: it has no recording path in this build and
 * the page shows it off and not editable (plan 16, WP-04). A spoken text that
 * is emptied is sent as '' and the line goes back to following the default
 * text (the server stores a text only when it differs from the default, plan
 * 16 WP-06, so the default keeps naming what the line really does).
 */
import type { OncallLine, OncallRotation, OncallSettings, UpdateLineInput } from '../../api/oncallApi';
import {
  MAX_ALERT_EMAILS,
  MAX_FALLBACK_NOTE_LENGTH,
  MAX_LINE_NAME_LENGTH,
  MAX_TEXT_LENGTH,
  SETTING_LIMITS,
} from './huntMath';
import type { IntSettingKey } from './huntMath';

export const INT_KEYS: IntSettingKey[] = ['ring_seconds', 'wave_size', 'max_passes', 'hold_message_every', 'callback_window_days'];
export const BOOL_KEYS = ['voicemail_enabled', 'transcription_enabled'] as const;
export const TEXT_KEYS = ['greeting_text', 'hold_text', 'voicemail_text'] as const;

export type BoolKey = (typeof BOOL_KEYS)[number];
export type TextKey = (typeof TEXT_KEYS)[number];

export interface SettingsFormState {
  name: string;
  fallback_note: string;
  /** The integer settings as typed (strings, so a half-typed number is not lost). */
  ints: Record<IntSettingKey, string>;
  bools: Record<BoolKey, boolean>;
  rotation: OncallRotation;
  texts: Record<TextKey, string>;
  alert_emails: string[];
}

/** Field keys the validation messages attach to. */
export type FormField = 'name' | 'fallback_note' | IntSettingKey | TextKey | 'alert_emails';
export type FormErrors = Partial<Record<FormField, string>>;

/** The server's email shape (line.js EMAIL_RE). */
const EMAIL_RE = /^[^\s@<>(),;:"[\]\\]+@[^\s@<>(),;:"[\]\\]+\.[^\s@<>(),;:"[\]\\]+$/;

export function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

export function isEmail(value: string): boolean {
  const email = normalizeEmail(value);
  return email.length > 0 && email.length <= 254 && EMAIL_RE.test(email);
}

/** Spaces collapsed and trimmed, the way the server compares and stores text. */
export function squash(value: string | null | undefined): string {
  return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
}

export function formFromLine(line: OncallLine): SettingsFormState {
  const s: Partial<OncallSettings> = line.settings ?? {};
  const ints = {} as Record<IntSettingKey, string>;
  for (const key of INT_KEYS) {
    const v = s[key];
    ints[key] = typeof v === 'number' && Number.isFinite(v) ? String(v) : String(SETTING_LIMITS[key].default);
  }
  return {
    name: line.name ?? '',
    fallback_note: line.fallback_note ?? '',
    ints,
    bools: {
      voicemail_enabled: s.voicemail_enabled !== false,
      transcription_enabled: s.transcription_enabled !== false,
    },
    rotation: s.rotation === 'fixed' ? 'fixed' : 'rotate',
    texts: {
      greeting_text: s.greeting_text ?? '',
      hold_text: s.hold_text ?? '',
      voicemail_text: s.voicemail_text ?? '',
    },
    alert_emails: Array.isArray(s.alert_emails) ? s.alert_emails.slice() : [],
  };
}

/** A typed integer, or null when it is not a whole number. */
export function parseWhole(value: string): number | null {
  const text = value.trim();
  if (!/^-?\d+$/.test(text)) return null;
  const n = Number(text);
  return Number.isSafeInteger(n) ? n : null;
}

export function validateForm(form: SettingsFormState): FormErrors {
  const errors: FormErrors = {};
  const name = squash(form.name);
  if (!name) errors.name = 'Give the line a name.';
  else if (name.length > MAX_LINE_NAME_LENGTH) errors.name = `At most ${MAX_LINE_NAME_LENGTH} characters.`;

  if (form.fallback_note.trim().length > MAX_FALLBACK_NOTE_LENGTH) {
    errors.fallback_note = `At most ${MAX_FALLBACK_NOTE_LENGTH} characters.`;
  }

  for (const key of INT_KEYS) {
    const { min, max } = SETTING_LIMITS[key];
    const n = parseWhole(form.ints[key]);
    if (n === null || n < min || n > max) errors[key] = `A whole number from ${min} to ${max}.`;
  }

  for (const key of TEXT_KEYS) {
    if (squash(form.texts[key]).length > MAX_TEXT_LENGTH) errors[key] = `At most ${MAX_TEXT_LENGTH} characters.`;
  }

  const emails = form.alert_emails.map(normalizeEmail);
  if (emails.some((e) => !isEmail(e))) errors.alert_emails = 'Every entry must be an email address.';
  else if (new Set(emails).size > MAX_ALERT_EMAILS) errors.alert_emails = `At most ${MAX_ALERT_EMAILS} addresses.`;
  return errors;
}

function sameEmails(a: string[], b: string[]): boolean {
  const x = a.map(normalizeEmail);
  const y = b.map(normalizeEmail);
  return x.length === y.length && x.every((e, i) => e === y[i]);
}

/**
 * PATCH /line's body for what changed between the loaded line and the form,
 * or null when nothing did. Call validateForm first: an invalid integer is
 * left out rather than guessed.
 */
export function patchFromForm(line: OncallLine, form: SettingsFormState): UpdateLineInput | null {
  const base = formFromLine(line);
  const patch: UpdateLineInput = {};

  const name = squash(form.name);
  if (name && name !== squash(base.name)) patch.name = name;

  const note = form.fallback_note.trim();
  const baseNote = base.fallback_note.trim();
  if (note !== baseNote) patch.fallback_note = note ? note : null;

  const settings: Partial<OncallSettings> = {};
  for (const key of INT_KEYS) {
    const n = parseWhole(form.ints[key]);
    if (n !== null && n !== parseWhole(base.ints[key])) settings[key] = n;
  }
  for (const key of BOOL_KEYS) {
    if (form.bools[key] !== base.bools[key]) settings[key] = form.bools[key];
  }
  if (form.rotation !== base.rotation) settings.rotation = form.rotation;
  for (const key of TEXT_KEYS) {
    const text = squash(form.texts[key]);
    if (text !== squash(base.texts[key])) settings[key] = text;
  }
  if (!sameEmails(form.alert_emails, base.alert_emails)) {
    const seen: string[] = [];
    for (const e of form.alert_emails.map(normalizeEmail)) if (!seen.includes(e)) seen.push(e);
    settings.alert_emails = seen;
  }
  if (Object.keys(settings).length > 0) patch.settings = settings;

  return Object.keys(patch).length > 0 ? patch : null;
}
